import { Message } from '@oneflowab/pomes';

import Button from 'components/button';
import EmptyState from 'components/empty-state';
import { useAiInsightsContext } from 'contexts/ai-insights-context';
import { ChartsWithSparkles } from '../assets/charts-with-sparkles';

import style from './disabled-state.module.scss';

const header = (
  <Message
    id="{insightsFeatureName} for documents"
    comment="Title on a page that shows when user does not have the analytics feature enabled for documents"
    values={{ insightsFeatureName: 'AI Review & Insights' }}
  />
);

type Props = {
  extensionDisabled?: boolean,
  hasMarketplaceAccess?: boolean,
  playbookDisabled?: boolean,
  hasPlaybookPermission?: boolean,
};

export const DisabledState = ({
  extensionDisabled,
  playbookDisabled,
  hasMarketplaceAccess,
  hasPlaybookPermission,
}: Props) => {
  const { workspaceId } = useAiInsightsContext();

  const extensionDisabledContent = (
    <>
      <p>
        <Message
          id="Improve the review process, uncover risks, and gain full control over the entire contract portfolio."
          comment="Description of the analytics feature that showcases its benefits"
        />
      </p>
      {hasMarketplaceAccess ? (
        <Button
          kind="primary"
          href="/marketplace/ai-insights"
          target="_blank"
          rel="noopener noreferrer"
          trackable={{
            name: 'Go To Marketplace Card',
            props: {
              location: 'ai insights tab',
              key: 'ai_review',
            },
          }}
        >
          <Message
            id="Enable in Marketplace"
            comment="CTA in a button to go to a page where user can enable the feature"
          />
        </Button>
      ) : (
        <Message
          id="Contact your Oneflow admin for access."
          comment="Showing when user does not have access to a feature"
        />
      )}
    </>
  );

  const playbookDisabledContent = (
    <>
      <p>
        <Message
          id="Set up the playbook for this workspace to speed up reviews, uncover hidden risks, and stay in control of the contract portfolio."
          comment="Description of the analytics feature that showcases its benefits"
        />
      </p>
      {hasPlaybookPermission ? (
        <Button
          kind="primary"
          href={`/admin/workspaces/${workspaceId}/ai-review`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Message
            id="Set up playbook"
            comment="CTA in a button to go to a page where user can enable the feature"
          />
        </Button>
      ) : (
        <Message
          id="Contact your workspace admin for access."
          comment="Showing when user does not have access to a feature"
        />
      )}
    </>
  );

  const getContent = () => {
    if (extensionDisabled) {
      return extensionDisabledContent;
    }
    if (playbookDisabled) {
      return playbookDisabledContent;
    }
    return <></>;
  };

  return (
    <div className={style.DisabledStateContainer}>
      <EmptyState
        icon={<ChartsWithSparkles width="260px" height="174px" />}
        className={style.DisabledState}
        header={<h1 className={style.Header}>{header}</h1>}
        content={<div className={style.Content}>{getContent()}</div>}
      />
    </div>
  );
};
